// Station side of receiving (M6, plan §9.1, §5.4; D-024, D-060): the deliveries this station
// received and what the server answered.
//
// A delivery is one `receipt.record` command in the outbox; in the same transaction a
// local-only `receipt_action` row records it (`pending`), with what the Receiving view needs to
// show it: the supplier, the order, each line with its lot (made here, so its label prints
// offline) and, for an order, what its lines had received on the station's last download
// (so the order's remaining quantities can take this delivery off until the next download
// brings the server's figures). The uploader writes the server's final answer onto the row
// (`applied`, `applied_with_flags`, `held`, `rejected`, with the answer's data and flags).
//
// A held delivery (D-066) is resolved by a manager in the Admin: booked (maybe corrected) or
// voided with a reason. The resolution comes down on the `station_holds` stream; the row then
// becomes `booked` or `voided` with the manager's note (review CR6-102), so the station
// stops showing it as held. The booked record itself comes down on `receiving_done`.
//
// Exactly once from the station too: the same receipt (one receipt id per receive dialog)
// can't be recorded twice here, unless the server refused the first; and the server books a
// receipt id only once whatever command carries it.

import type { CommandResponse } from '@enp/domain';
import type { LocalEffect, LocalReader, LocalTx } from './engine.js';

/** Where a delivery stands: `pending` until the server's final answer. */
export type ReceiptActionStatus =
  | 'pending'
  | 'applied'
  | 'applied_with_flags'
  | 'held'
  | 'rejected'
  /** Held, then booked by a manager in the Admin (D-066). */
  | 'booked'
  /** Held, then voided by a manager with a reason: nothing was booked (D-066). */
  | 'voided';

/** A flag (the server's codes), with what it is about. */
export interface ReceiptFlag {
  code: string;
  data?: Record<string, unknown>;
}

/** One received line, as the station recorded it. */
export interface ReceiptLineDetail {
  line_id: string;
  product_id: string;
  product_name: string;
  /** The product's base unit. */
  base_uom: string | null;
  /** The order line the station matched it to (its own preview), if any. */
  po_line_id: string | null;
  supplier_item_id: string | null;
  /** As counted, in `uom`. */
  qty: string;
  uom: string;
  /** In the base unit, as the station worked it out; null when it couldn't. */
  base_qty: string | null;
  supplier_lot_ref: string | null;
  best_before: string | null;
  /** The lot made here: its id and `L-…` code (the label prints offline). */
  lot_id: string;
  lot_code: string;
  /** The state printed on the label (the product's first state). */
  state: string;
  /** The flags the station showed before it was saved. */
  flags: ReceiptFlag[];
}

/** A delivery as the station recorded it (kept after its order leaves the stream). */
export interface ReceiptDetail {
  number: string;
  po_id: string | null;
  po_number: string | null;
  supplier_id: string | null;
  supplier_name: string | null;
  location_id: string;
  location_name: string | null;
  supplier_doc_ref: string | null;
  lines: ReceiptLineDetail[];
  /** Receipt-level flags the station showed (e.g. `not_delivered`). */
  flags: ReceiptFlag[];
  /**
   * Per order line: its `received_base_qty` as last downloaded when this delivery was
   * saved. While the synced figure still reads the same, the server's answer hasn't come
   * down yet and the station adds this delivery itself.
   */
  po_received_before: Record<string, string>;
}

/** The server's final answer, as kept on the row. */
export interface ReceiptAnswer {
  data?: Record<string, unknown>;
  flags: { code: string; message?: string; data?: Record<string, unknown> }[];
  reason?: string;
  message?: string;
  hold_id?: string;
  /** How a manager resolved the hold (CR6-102): booked or voided, with their note. */
  resolution?: HoldResolution;
}

/** A manager's resolution of a held delivery, as it came down on `station_holds`. */
export interface HoldResolution {
  status: 'booked' | 'voided';
  /** The manager's note: the reason for a void; optional for a booking. */
  note: string | null;
  resolved_at: string | null;
}

/** A `receipt_action` row as stored (JSON columns as text). */
export interface ReceiptActionRow {
  id: string;
  receipt_id: string;
  po_id: string | null;
  staff_id: string | null;
  at: string;
  day: string;
  detail: string | null;
  status: ReceiptActionStatus;
  answer: string | null;
  answered_at: string | null;
}

/** This receipt is already recorded on this station (a double tap). */
export class ReceiptAlreadyRecordedError extends Error {
  constructor(readonly receiptId: string) {
    super(`This station already recorded receipt ${receiptId}`);
    this.name = 'ReceiptAlreadyRecordedError';
  }
}

/** The delivery recorded here under `receiptId` (any status but `rejected`), if any. */
export function findReceiptAction(
  db: LocalReader,
  receiptId: string,
): Promise<ReceiptActionRow | null> {
  return db.getOptional<ReceiptActionRow>(
    `SELECT * FROM receipt_action WHERE receipt_id = ? AND status != 'rejected'
      ORDER BY at DESC LIMIT 1`,
    [receiptId],
  );
}

/**
 * Records a delivery, in the transaction of the command that sends it. Throws
 * {@link ReceiptAlreadyRecordedError} (rolling the command back) when the same receipt is
 * already recorded here and the server hasn't refused it.
 */
export async function recordReceiptAction(
  tx: LocalTx,
  row: {
    commandId: string;
    receiptId: string;
    poId: string | null;
    staffId: string;
    at: string;
    day: string;
    detail: ReceiptDetail;
  },
): Promise<void> {
  if (await findReceiptAction(tx, row.receiptId)) {
    throw new ReceiptAlreadyRecordedError(row.receiptId);
  }
  await tx.execute(
    `INSERT INTO receipt_action (id, receipt_id, po_id, staff_id, at, day, detail, status, answer, answered_at)
     VALUES (?, ?, ?, ?, ?, ?, ?, 'pending', NULL, NULL)`,
    [
      row.commandId,
      row.receiptId,
      row.poId,
      row.staffId,
      row.at,
      row.day,
      JSON.stringify(row.detail),
    ],
  );
}

/**
 * A command's local effect that records the delivery (for `StationData.enqueue`). `detail`
 * is made from the envelope, so it carries the numbers the command took (`GR-…`, `L-…`).
 */
export function receiptActionEffect(options: {
  receiptId: string;
  poId: string | null;
  day: string;
  detail: (payload: unknown) => ReceiptDetail;
}): LocalEffect {
  return (tx, envelope) =>
    recordReceiptAction(tx, {
      commandId: envelope.command_id,
      receiptId: options.receiptId,
      poId: options.poId,
      staffId: envelope.staff_id,
      at: envelope.occurred_at,
      day: options.day,
      detail: options.detail(envelope.payload),
    });
}

/**
 * Writes a final answer onto its delivery (called by the uploader for every final answer; a
 * command that isn't a delivery has no row and nothing changes). Transient answers never
 * reach here: the command stays `pending` and is sent again.
 */
export async function recordReceiptAnswer(
  tx: LocalTx,
  answer: CommandResponse,
  now: Date,
): Promise<void> {
  const id = answer.command_id;
  if (!id) return;
  if (
    answer.status !== 'applied' &&
    answer.status !== 'applied_with_flags' &&
    answer.status !== 'held' &&
    answer.status !== 'rejected'
  ) {
    return;
  }
  const kept: ReceiptAnswer = {
    flags: 'flags' in answer && Array.isArray(answer.flags) ? answer.flags : [],
    ...('data' in answer && answer.data ? { data: answer.data } : {}),
    ...('reason' in answer && typeof answer.reason === 'string' ? { reason: answer.reason } : {}),
    ...('message' in answer && typeof answer.message === 'string'
      ? { message: answer.message }
      : {}),
    ...(answer.status === 'held' ? { hold_id: answer.hold_id } : {}),
  };
  await tx.execute(
    'UPDATE receipt_action SET status = ?, answer = ?, answered_at = ? WHERE id = ?',
    [answer.status, JSON.stringify(kept), now.toISOString(), id],
  );
}

/**
 * Deliveries to show: received on `day`, or still waiting for the server or kept for a
 * manager (any day). Oldest first.
 */
export function readReceiptActions(db: LocalReader, day: string): Promise<ReceiptActionRow[]> {
  return db.getAll<ReceiptActionRow>(
    `SELECT * FROM receipt_action WHERE day = ? OR status IN ('pending', 'held')
      ORDER BY at, id`,
    [day],
  );
}

/** Held deliveries whose hold a manager has resolved (stream `station_holds`, CR6-102). */
export const RESOLVED_HELD_RECEIPTS_SQL = `SELECT r.id, r.answer, h.status AS hold_status,
    h.resolution_note, h.resolved_at
  FROM receipt_action r
  JOIN command_hold h ON h.id = json_extract(r.answer, '$.hold_id')
  WHERE r.status = 'held' AND h.status IS NOT NULL AND h.status != 'open'`;

/**
 * Writes a manager's resolution onto held deliveries (CR6-102): `booked` when the hold was
 * applied, `voided` when it was discarded, keeping the manager's note with the answer.
 * Returns how many changed.
 */
export async function resolveHeldReceipts(tx: LocalTx): Promise<number> {
  const rows = await tx.getAll<{
    id: string;
    answer: string | null;
    hold_status: string;
    resolution_note: string | null;
    resolved_at: string | null;
  }>(RESOLVED_HELD_RECEIPTS_SQL);
  for (const r of rows) {
    const status = r.hold_status === 'applied' ? 'booked' : 'voided';
    let answer: ReceiptAnswer = { flags: [] };
    try {
      if (r.answer) answer = JSON.parse(r.answer) as ReceiptAnswer;
    } catch {
      // A damaged answer keeps only the resolution.
    }
    answer.resolution = { status, note: r.resolution_note, resolved_at: r.resolved_at };
    await tx.execute('UPDATE receipt_action SET status = ?, answer = ? WHERE id = ?', [
      status,
      JSON.stringify(answer),
      r.id,
    ]);
  }
  return rows.length;
}
